/**
 * Release notes — the source of truth for the in-app "What's New" modal and the
 * /whats-new changelog page. Newest release first.
 *
 * On each release: add a new entry at the TOP and the modal will show once to
 * every user whose last-seen version is behind it. Keep the wording
 * user-facing and benefit-led; mark Pro-only items with `pro: true`.
 */

export interface ReleaseHighlight {
  text: string;
  /** Pro/Enterprise-only capability — shown with a small "Pro" tag. */
  pro?: boolean;
}

export interface ReleaseNote {
  /** Marketing/release version, e.g. '1.12'. */
  version: string;
  /** Human date, e.g. 'October 2026'. */
  date: string;
  /** Short headline for the release. */
  title: string;
  highlights: ReleaseHighlight[];
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: '1.17',
    date: 'October 2026',
    title: 'Sharper estimates, faster answers',
    highlights: [
      {
        text: 'Fixed the Estimator capacity check so it no longer flags every role as over-allocated. It now compares your estimated team against the people you’ve actually assigned to roles — and stays quiet when your workspace isn’t tracking role headcount yet.',
      },
      {
        text: 'The in-app User Guide search is much smarter — type several words and it finds the sections that contain all of them, ranks the best matches first, and highlights every term. Press “/” to jump to search, Enter to open the top result, Esc to clear.',
      },
    ],
  },
  {
    version: '1.16',
    date: 'October 2026',
    title: 'Change control your way',
    highlights: [
      {
        text: 'The change-request approval workflow is now optional per workspace — turn it off in Settings → Workflow and a project manager approves and applies changes directly, with no approval steps.',
      },
      {
        text: 'Need more than one sign-off? Set how many approvals a change request requires. It only becomes Approved once that many distinct approvers have approved it, with live "1 of 2" progress.',
      },
      {
        text: 'Re-baseline from a change request: tick a box on the CR, and when it is approved ProjexHQ updates the project automatically — budget, end date and scope move by the change’s impact and a new baseline is captured and linked to the CR.',
      },
      {
        text: 'New Go-Live tab on every project — an optional cutover / launch plan with seven sections (scope & strategy, go/no-go, cutover & migration, rollback, user readiness, hypercare, and post-launch review). Fill in what’s useful; nothing is required.',
      },
      {
        text: 'Put a project On hold, mark it Completed, or Cancel it any time — project status is now editable from Edit Project Details, and the new Cancelled state flows through the Projects filter, the status-mix chart and the Portfolio Status Report.',
      },
    ],
  },
  {
    version: '1.15.2',
    date: 'October 2026',
    title: 'Real earned-value reporting',
    highlights: [
      {
        text: 'CPI and SPI are now real earned-value metrics — computed from your budget, task progress, baseline and logged time — instead of placeholders. When the data isn’t there yet they show “—” rather than a flattering 1.00.',
      },
      {
        text: 'Earned value now rolls up your task costs, and a project’s actual cost can be drawn from logged timesheet hours × rate — so cost and schedule performance reflect what’s really happening.',
      },
      {
        text: 'Refreshed the in-app User Guide so it matches the current app.',
      },
    ],
  },
  {
    version: '1.15.1',
    date: 'October 2026',
    title: 'Fixes & polish',
    highlights: [
      {
        text: 'Fixed a crash that could blank out the Settings screen. Settings and all its tabs now open reliably.',
      },
      {
        text: 'Signing up with an email that’s already registered now tells you right away so you can log in instead, rather than waiting for a verification email that never arrives.',
      },
    ],
  },
  {
    version: '1.15',
    date: 'October 2026',
    title: 'Enterprise resource management',
    highlights: [
      {
        text: 'New Enterprise resource mode in Settings → Resources — turn it on to unlock the features below. It’s off by default, so nothing changes for your workspace unless you opt in.',
        pro: true,
      },
      {
        text: 'Rate cards: set standard cost (and optional bill) rates by role and seniority grade. They fill in a cost for anyone who doesn’t have a personal rate, so budgets and estimates stay consistent across large teams.',
        pro: true,
      },
      {
        text: 'People can now hold a primary plus secondary roles, and every skill can carry a proficiency level (novice → expert).',
        pro: true,
      },
      {
        text: 'Managed departments: give each department an owner (Resource Manager), a cost center, a parent, and its own utilisation targets. Resource Managers can curate resources without full admin.',
        pro: true,
      },
    ],
  },
  {
    version: '1.14',
    date: 'October 2026',
    title: 'Faster project setup',
    highlights: [
      {
        text: 'Change a project’s methodology any time — a Methodology dropdown is now on the Overview tab, right next to Portfolio, Program and Size.',
      },
      {
        text: 'Create a portfolio or program without leaving the New Project dialog — pick “+ New portfolio…” or “+ New program…” straight from the dropdown, and a new program nests under the portfolio you’ve chosen.',
      },
    ],
  },
  {
    version: '1.13',
    date: 'October 2026',
    title: 'Smarter capacity planning',
    highlights: [
      {
        text: 'Capacity & Resources: click any Heat Map cell to see the projects and tasks behind the number — and open that person’s full profile from there.',
      },
      {
        text: 'Time off and company holidays now count against capacity, so a person’s utilisation reflects who’s actually available.',
      },
      {
        text: 'Set your own red / amber / green utilisation thresholds for the whole workspace in Settings → Workflow → Health thresholds.',
      },
      {
        text: 'Release notes are now in the app — this “What’s New” note, plus a full changelog under What’s New in the sidebar.',
      },
    ],
  },
  {
    version: '1.12',
    date: 'October 2026',
    title: 'AI assistance and a smarter Status Report',
    highlights: [
      {
        text: 'AI drafting across your projects: generate a project’s executive summary and next steps, draft a risk’s mitigation plan, summarize a change request’s impact, and get a plain-language read on schedule health — each written from your own project data.',
        pro: true,
      },
      {
        text: '“Not sure?” methodology recommendation built into New Project — answer a few quick sliders and get a scored Agile / Waterfall / Hybrid pick that fills the field for you.',
      },
      {
        text: 'Status Report now shows red / amber / green health on every KPI.',
      },
      {
        text: 'Decisions are pulled automatically from your Decisions register and scoped to the reporting period, so the report stays current — with a per-report hide for anything you don’t want shown.',
      },
      {
        text: 'Issues that materialised from a risk are now traced back to the originating risk.',
      },
      {
        text: 'Set the red / amber / green health thresholds for your whole workspace in Settings → Workflow → Health thresholds.',
      },
      {
        text: 'Updated in-app User Guide, plus refinements throughout.',
      },
    ],
  },
  {
    version: '1.11',
    date: 'September 2026',
    title: 'Methodology Intelligence',
    highlights: [
      {
        text: 'New: the Methodology Advisor — answer a few quick questions and get a scored Agile / Waterfall / Hybrid recommendation for your project, then apply it in one click.',
      },
      {
        text: 'Get an AI explanation of why the recommended approach fits, what to watch for, and how to adapt it for your team.',
        pro: true,
      },
    ],
  },
  {
    version: '1.10',
    date: 'September 2026',
    title: 'Scheduling and date fixes',
    highlights: [
      {
        text: 'Fixed: Finish-to-Start dependencies now schedule the successor to start the day after the predecessor finishes.',
      },
      {
        text: 'Fixed: project start and end dates could display a day earlier than entered in some time zones.',
      },
      {
        text: 'The task editor no longer closes when you click outside it, so an in-progress edit isn’t lost.',
      },
    ],
  },
  {
    version: '1.9',
    date: 'September 2026',
    title: 'Import your team and resources',
    highlights: [
      {
        text: 'Bulk-import project resources from a CSV or Excel file — name, email, role, department, capacity and rate — from the Resources page or the Resource Registry.',
      },
      {
        text: 'Optionally send workspace invites to the people you import in the same step.',
      },
    ],
  },
  {
    version: '1.8',
    date: 'September 2026',
    title: 'Estimating tool',
    highlights: [
      {
        text: 'New Estimator: turn project size, complexity and methodology into an effort, duration, phased schedule and cost estimate — with a capacity check — then hand it off to a new project, demand or roadmap.',
      },
      {
        text: 'Undo: deleting a task, risk, issue, change or budget line now shows a 10-second “Undo” so an accidental delete is easy to reverse.',
      },
    ],
  },
  {
    version: '1.7',
    date: 'September 2026',
    title: 'Navigation & UX overhaul',
    highlights: [
      {
        text: 'Restructured sidebar grouped by what you do, breadcrumbs on every detail page, and a “+ New” menu to start anything from anywhere.',
      },
      {
        text: 'New command palette — press ⌘K (Ctrl + K) to jump to any project, risk, change or report instantly.',
      },
      {
        text: 'Collapsible sidebar, a Comfortable/Compact density toggle, and smarter scrolling when a project has many tabs.',
      },
    ],
  },
  {
    version: '1.6',
    date: 'September 2026',
    title: 'Budget import',
    highlights: [
      {
        text: 'Import budget lines from a spreadsheet instead of entering them by hand.',
      },
    ],
  },
  {
    version: '1.5',
    date: 'September 2026',
    title: 'Schedule import',
    highlights: [
      {
        text: 'Import a project schedule from a spreadsheet to get a WBS and Gantt going quickly.',
      },
    ],
  },
  {
    version: '1.4',
    date: 'August 2026',
    title: 'Android stability',
    highlights: [
      {
        text: 'Fixed a crash that could occur on launch for some Android devices.',
      },
    ],
  },
  {
    version: '1.1',
    date: 'August 2026',
    title: 'Paid modules, charts & AI briefings',
    highlights: [
      {
        text: 'Introduced the paid PPM modules and richer charts across the workspace.',
      },
      {
        text: 'AI-generated Executive and Steering Committee briefings, drafted from your live portfolio and project data.',
        pro: true,
      },
    ],
  },
  {
    version: '1.0',
    date: 'July 2026',
    title: 'ProjexHQ launches',
    highlights: [
      {
        text: 'The first release: portfolios, programs and projects; schedules and Gantt; risks, issues, changes and decisions; budgets; resources; and the reports that summarise it all.',
      },
    ],
  },
];

/** The latest release version — what the modal gates on. */
export const CURRENT_RELEASE_VERSION = RELEASE_NOTES[0]?.version ?? '';
